'use client'

import { SidePanelClose, SidePanelOpen } from '@carbon/icons-react'
import { Tooltip } from 'app/_components/Tooltip'
import SidebarTrigger from 'app/_components/WelcomePageLayout/SidebarTrigger'
import { cx } from 'class-variance-authority'
import React from 'react'

type WidgetContainerProps = {
  children: React.ReactNode
  align: 'left' | 'right'
  collapsed: boolean
  toggleAction: () => void
  title: React.ReactNode
  subtitle?: React.ReactNode
  closedText?: React.ReactNode
  additionalActions?: React.ReactNode
  overflowY?: 'auto' | 'hidden'
}

export default function WidgetContainer({
  children,
  align,
  collapsed,
  toggleAction,
  title,
  subtitle,
  closedText,
  additionalActions,
  overflowY = 'auto',
}: WidgetContainerProps) {
  let alignStyle
  let Icon

  switch (align) {
    case 'left':
      alignStyle = 'slide-from-left xl:!border-r-[1px]'
      Icon = collapsed ? SidePanelOpen : SidePanelClose
      break
    case 'right':
      alignStyle = 'slide-from-right xl:!border-[1px]'
      Icon = collapsed ? SidePanelClose : SidePanelOpen
      break
  }

  return (
    <div
      className={cx(
        'border-gray-disabled flex min-h-full w-full items-center gap-4 border-b bg-white px-4 pt-4 pb-2 sm:pt-6 sm:pb-4',
        alignStyle,

        collapsed
          ? 'flex-row-reverse justify-between xl:w-fit xl:flex-col xl:justify-normal'
          : 'flex-col xl:w-[400px]',
      )}
    >
      <div
        className={cx(
          'flex items-center justify-between',
          !collapsed && 'w-full',
        )}
      >
        <span className={collapsed ? 'hidden' : undefined}>{title}</span>
        <div className="flex">
          {!collapsed && additionalActions}
          <Tooltip
            content={collapsed ? 'Ausklappen' : 'Einklappen'}
            side="left"
          >
            <SidebarTrigger
              aria-label={collapsed ? 'Ausklappen' : 'Einklappen'}
              toggleAction={toggleAction}
              className={cx(
                collapsed ? 'text-icon-interactive' : 'text-icon-primary',
                'rotate-90 xl:rotate-0',
              )}
              title={collapsed ? 'Ausklappen' : 'Einklappen'}
            >
              <Icon aria-hidden="true" />
            </SidebarTrigger>
          </Tooltip>
        </div>
      </div>
      <div className={cx('w-full text-left', collapsed && 'hidden')}>
        {subtitle}
      </div>
      <div
        className={cx(
          'hidden xl:flex-col-reverse xl:items-center xl:justify-center!',
          collapsed && 'block!',
        )}
      >
        <span className="mt-2 text-xl xl:text-xs">{closedText}</span>
      </div>
      {!collapsed && (
        <div
          className={`flex h-full w-full gap-4 overflow-y-${overflowY} overflow-x-hidden`}
        >
          {children}
        </div>
      )}
    </div>
  )
}
